/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
 
// Custom ColorGroup HTML element script for grouped colour swatches

var ColorGroup = class extends HTMLElement {
  // runs each time a color-group element is added to the DOM
  connectedCallback() {
    const { allColors, activeColorHandles } = this._getActiveColors();

    this._initColorGroups(activeColorHandles, allColors);
    this._initTagRemoval();

    // listen for filterLoaded custom event dispatched by the ProductFacet Class in theme.js
    document.addEventListener("filterLoaded", this._filtersLoaded.bind(this));
  }

  _getActiveColors() {
    const url = new URL(window.location);
    const allColors = [];
    const activeColors = [];
    const activeColorHandles = [];

    url.searchParams.forEach((value, key, parent) => {
      if (key === "filter.v.option.color") {
        activeColors.push(value);
      }
    });

    // get the relevant group handles for the active colors

    document.querySelectorAll("input[data-color]").forEach((element) => {
      if (activeColors.includes(element.value)) {
        activeColorHandles.push(element.dataset.color);
      }
      allColors.push(element.dataset.color);
    });

    return {
      allColors,
      activeColorHandles,
    };
  }

  _filtersLoaded(event, target) {
    const { allColors, activeColorHandles } = this._getActiveColors();
    this._initColorGroups(activeColorHandles, allColors);
    this._initTagRemoval();
    // update all count bubbles with new value - some do not reload
    const newCountElement = document.querySelector(
      "#facet-filters [data-active-filter-count]"
    );
    if (newCountElement) {
      const countBubbles = document.querySelectorAll(
        "[data-active-filter-count]"
      );
      countBubbles.forEach((element, index) => {
        element.textContent = newCountElement.dataset.activeFilterCount;
      });
    }
  }

  _setColors(event, target) {
    const attribute = event.target.getAttribute("data-color-group");
    const colors = event.target.getAttribute("data-colors").split(",");
    const isChecked = event.target.checked;

    for (const color of colors) {
      const colourString = color.trim();
      const matched = document.querySelectorAll(`[data-color=${colourString}]`);
      if (matched.length > 0) {
        for (const match of matched) {
          match.checked = isChecked;
          match.dispatchEvent(
            new CustomEvent("change", {
              bubbles: true,
            })
          );
        }
      }
    }
  }

  _initColorGroups(activeColorHandles = null, allColors = null) {
    const activeColorGroupTitles = document.getElementById(
      "active-color-group-titles"
    );
    const colorGroupCheckboxes =
      document.querySelectorAll("[data-color-group]");

    // set the selected ones to match active filters
    if (activeColorHandles) {
      const activeGroupLabels = [];
      colorGroupCheckboxes.forEach((element) => {
        var groupColours = element.dataset.colors.split(",");

        const activeMatches = groupColours.filter((value) =>
          activeColorHandles.includes(value)
        );

        if (activeMatches.length > 0) {
          element.checked = true;
          activeGroupLabels.push(element.title);
        }
      });
      if (activeColorGroupTitles) {
        activeColorGroupTitles.innerHTML = activeGroupLabels.join(", ");
      }
    }

    if (allColors) {
      colorGroupCheckboxes.forEach((element) => {
        var groupColours = element.dataset.colors.split(",");

        // get individual colors for this group
        const allMatches = groupColours.filter((value) =>
          allColors.includes(value)
        );

        if (allMatches.length === 0) {
          element.parentElement.classList.add("visually-hidden");
        }

        // check if all colors belonging to the group are disabled and if so disable the group color too
        const enabledCheckboxes = groupColours.filter((value) => {
          if (value) {
            const cb = document.querySelector(`[data-color=${value}]`);
            return cb && !cb.disabled;
          }
          return false;
        });

        // disable the group checkbox and add disabled class to the parent element
        element.disabled = enabledCheckboxes.length < 1;
        if (element.disabled) {
          element.parentElement.classList.add("is-disabled");
        } else {
          element.parentElement.classList.remove("is-disabled");
        }
      });
    }

    for (var i = 0; i < colorGroupCheckboxes.length; i++) {
      colorGroupCheckboxes[i].addEventListener(
        "change",
        this._setColors,
        false
      );
    }
  }

  _initTagRemoval() {
    const colorTags = document.querySelectorAll("[data-clear-group]");
    colorTags.forEach((element) => {
      element.addEventListener("click", (e) => {
        e.preventDefault();
        const groupCheckbox = document.getElementById(
          element.dataset.clearGroup
        );
        groupCheckbox.checked = false;
        groupCheckbox.dispatchEvent(
          new CustomEvent("change", {
            bubbles: true,
          })
        );
      });
    });
  }
};

window.customElements.define("color-group", ColorGroup);

// Custom event handlers for variant image display

var handleVariantChange = (e) => {
  // event detail includes the productForm object from whcih we can get the selected checkbox and the corresponding colour handle
  const allVariantImages = document.querySelectorAll(
    ".product__media-list--desktop img[data-color]"
  );
  const allModel = document.querySelectorAll(
    ".model"
  );
  const productForm = e.detail.productForm;

  allVariantImages.forEach((el) => {
    el.closest(".product__media-item").classList.add("visually-hidden");
  });
  
  allModel.forEach((el) => {
    el.classList.add("visually-hidden");
  });

  let colorHandle = null;
  let modelId = null;

  for (let [key, value] of Object.entries(productForm)) {
    if (value.checked && value.dataset.colorHandle) {
      colorHandle = value.dataset.colorHandle;
      if (value.dataset.modelId){
      	modelId = value.dataset.modelId;
      }
      break;
    }
  }
  if (colorHandle) {
//     console.log(colorHandle);
    const variantImages = document.querySelectorAll(
      `.product__media-list--desktop img[data-color='${colorHandle}']`
    );
    variantImages.forEach((el) => {
      el.closest(".product__media-item").classList.remove("visually-hidden");
      if (variantImages.length == 1) {
        el.closest(".product__media-item").style.width = '100%';
      }
    });
  }
  if (modelId) {
    const variantModel = document.querySelectorAll(
      `.model[data-model-id='${modelId}']`
    );
    variantModel.forEach((el) => {
      el.classList.remove("visually-hidden");
    });
    
//     console.log(variantModel.length);
    
    if (variantModel && variantModel.length > 0) {
      if (document.querySelector(".model--no-model")){
      	document.querySelector(".model--no-model").classList.add("visually-hidden");
      }
      if (document.querySelector(".product-tabs__model-block")){
      	document.querySelector(".product-tabs__model-block").classList.remove("visually-hidden");
      }
    } 
  } else {
      if (document.querySelector(".model--no-model")){
        document.querySelector(".model--no-model").classList.remove("visually-hidden");
      }
      if (document.querySelector(".product-tabs__model-block")){
        document.querySelector(".product-tabs__model-block").classList.add("visually-hidden");
      }
    }
};

  // js/helper/currency.js
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

document.addEventListener("variantChanged", handleVariantChange.bind(this));

// Slider custom click(Whole Slide URL in castomize)
$(function() {
  $('[data-slidelink]').click(function(event){
    event.preventDefault();
    var link = $(this).closest('[data-slidelink]').data('slidelink');
    if ($(this).hasClass("is-selected") && event.currentTarget.localName == 'hero-banner-item'){
        if (event.target.href){
          window.location.href = event.target.href;
        } else {
          window.location.href = link;
        }
      } else {
        if (!$(this).attr("hidden") && event.currentTarget.localName == 'slide-show-item'){
          if (event.target.href){
        	window.location.href = event.target.href;
          } else {
          	window.location.href = link;
          }
        }
      }
  });
  let clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;
  if (clientWidth >= 1000){
    $('[data-zoom]').zoom({ on:'click' });
  }
  
    // $('[data-zoom]').zoom({
    //   url: $(this).data("zoom")
    // });

  // $('[image-zoom]').click(function(event){
  //   event.preventDefault
  //   $('.product__media-list--desktop .product__media-item').removeClass('is-selected');
  //   $(this).parents('.product__media-item').addClass('is-selected');
  //   $('.product__zoom-button').trigger('click');
  // });
})

// document.addEventListener('variant:added', (e)=>{

//   var variantMetafieldTitle = document.querySelector('.quick-buy-product__info .product-item-meta__title').textContent;
//   var variantCompareAtPrice = document.querySelector('.quick-buy-product__info .price.price--highlight') !== null;
//   var variantRRP = document.querySelector('.quick-buy-product__info .price.price--compare') !== null;
//   var variantSaving = document.querySelector('.quick-buy-product__info .price .price--saving') !== null;

//   // wait for the content of the drawer to be set first
//   setTimeout(() => {
//     const qsd = document.querySelector('.cross-sell.drawer__content.visible');
//     const currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;

//     if (!variantCompareAtPrice.textContent) {
//       var priceHtml = `<span class="price">${formatMoney(e.detail.variant.price, currencyFormat)}</span>`;
//     } else if (!variantSaving.textContent) {
//       var priceHtml = `
//         <div class="price-list">
//           <span class="price price--highlight">${formatMoney(e.detail.variant.price, currencyFormat)}</span>
//           <span class="price price--compare">${variantRRP.textContent}</span>
//       </div>
//       `;
//     } else {
//       var priceHtml = `
//       <div class="price-list">
//         <span class="price price--highlight">${formatMoney(e.detail.variant.price, currencyFormat)}
//           <span class="price--saving-compact">${variantSaving.textContent}</span>          
//         </span>
//         <span class="price price--compare">${variantRRP.textContent}</span>
//     </div>
//     `;
//     }

//     if(qsd) {
//       const itemAddedHtml = `
//       <div class="item-added-container">
//         <tr class="line-item">
//           <td class="line-item__product">
//             <div class="line-item__content-wrapper line-item__content-wrapper--tight">
//               <a href="${e.detail.variant.url}" class="line-item__image-wrapper" tabindex="-1" aria-hidden="true">
//                 <img class="line-item__image" loading="sizes" src="${e.detail.variant.image}">
//               </a>
//               <div class="item-added-meta-grid">
//                 <a href="${e.detail.variant.url}" class="product-item-meta__title text--small">${variantMetafieldTitle} </a>
//                 <div class="product-item-meta__property-list">
//                   <span class="product-item-meta__property text--subdued text--xsmall">${e.detail.variant.variant_title}</span>
//                 </div>
//                 ${priceHtml}
//               </div>
//             </div>
//           </td>
//         </tr>
//       </div>
//       `;
//       qsd.insertAdjacentHTML("beforebegin", itemAddedHtml);
//     }
//   }, 500);

// });